import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { useAdminNotifications } from '../hooks/useAdminNotifications';
import { NotificationBell } from './admin/NotificationBell';
import { NotificationDrawer } from './admin/NotificationDrawer';
import { COLORS, FONTS } from '../styles/tokens';

/** A single secondary-nav tab within a section. */
export interface SectionTab {
  label: string;
  path: string;
  /** Exact-match only (use for a section's home tab whose path prefixes its children). */
  end?: boolean;
}

/**
 * Sub-layout for a top-level section: renders the section's secondary-nav
 * tabs + the notification bell, with an Outlet for the active page. The tab
 * set is passed in so each section (Apps, Users, Admin) supplies its own.
 */
export function SectionLayout({ tabs }: { tabs: SectionTab[] }): React.JSX.Element {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { items, unread, markAllAsRead, markOneAsRead } = useAdminNotifications(token);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '0 0 16px',
          borderBottom: `1px solid ${colors.border}`,
          marginBottom: '20px',
          minWidth: 0,
        }}
      >
        <div className="vx-tab-scroller" style={{ flex: 1, display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.end}
              style={({ isActive }) => ({
                padding: '8px 16px',
                borderRadius: '6px',
                fontFamily: FONTS.heading,
                fontSize: '12px',
                letterSpacing: '0.15em',
                textTransform: 'uppercase' as const,
                textDecoration: 'none',
                color: isActive ? '#FFFFFF' : (isDark ? '#D0D0D0' : '#333333'),
                backgroundColor: isActive
                  ? (isDark ? 'rgba(1, 169, 237, 0.15)' : 'rgba(1, 101, 198, 0.1)')
                  : 'transparent',
                transition: 'background-color 0.2s, color 0.2s',
              })}
            >
              {tab.label}
            </NavLink>
          ))}
        </div>
        <NotificationBell
          unread={unread}
          open={drawerOpen}
          onToggle={() => setDrawerOpen(value => !value)}
          textPrimary={colors.text}
          textSecondary={colors.textSecondary}
          border={colors.border}
        />
      </nav>

      <div style={{ flex: 1, overflow: 'auto' }}>
        <Outlet />
      </div>

      <NotificationDrawer
        open={drawerOpen}
        items={items}
        colors={{ text: colors.text, textSecondary: colors.textSecondary, card: colors.card, border: colors.border, bg: colors.bg }}
        onClose={() => setDrawerOpen(false)}
        onMarkAllRead={() => { void markAllAsRead(); }}
        onMarkOneRead={id => { void markOneAsRead(id); }}
      />
    </div>
  );
}
